/**
 * Highlight regions for the Preview page (Issue #2254).
 *
 * The Preview highlights part of a plan sheet by dimming everything else. Until this issue a
 * link could name one box only: `?highlight=x,y,width,height`. A plan-review explanation usually
 * points at several things on one sheet at once (two receptacles and the sink outlet between
 * them), so a link can now carry several boxes, and a label for each:
 *
 *   ?highlight=120,340,200,90&highlightAlso=900,340,180,90;500,700,60,40&highlightLabels=Sink;Range;Outlet
 *
 * - `highlight` is the first box, in exactly the old format, so every existing link keeps working.
 * - `highlightAlso` carries the other boxes, separated by `;`.
 * - `highlightLabels` is optional. It names the boxes in order, the `highlight` box first. A box
 *   may have no label: leave its place empty (`highlightLabels=Sink;;Outlet`).
 * - Coordinates are whole PDF points on the page as displayed, that is, with its rotation applied.
 * - `highlightRotation` (Issue #2445) states which page rotation the coordinates were measured at:
 *   0, 90, 180 or 270. A page can be turned after a link was written, and the same numbers then
 *   point at different content. With the rotation in the link, the Preview turns the boxes onto the
 *   page as it is displayed now. A link without it is read as it always was, as matching the page
 *   as displayed when the link is first opened.
 * - `highlightZoom` (Issue #3256) asks for a closer view than the standard highlight framing: 2
 *   frames the highlight twice as close, centred on it. The assistant sends it when the user asks
 *   to zoom in on what is already highlighted. A link without it gets the standard framing.
 *
 * Why the first box stays in `highlight` instead of all boxes sharing one parameter: links are
 * written into diagrams and documents and opened later against whichever build an environment
 * happens to serve. A build from before this issue reads `highlight` as one box and ignores the
 * parameters it does not know, so it still highlights the first region. Had all the boxes been
 * put in `highlight`, that build would have failed to read it and highlighted nothing.
 *
 * This module is pure (no Angular), so the URL handling, the tab service and the overlay can all
 * share it and it can be tested without a component.
 */

/** A rectangle in page pixel coordinates. */
export interface HighlightBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One highlighted region: where it is, and optionally what it is. */
export interface HighlightRegion {
  box: HighlightBox;
  label?: string;
}

/** Units used by the boxes in a highlight link. Existing links use PDF points. */
export type HighlightUnits = 'points' | 'permille';

/** Query parameter that carries the first box. */
export const HIGHLIGHT_QUERY_PARAM = 'highlight';

/** Query parameter that carries the boxes after the first. */
export const HIGHLIGHT_ALSO_QUERY_PARAM = 'highlightAlso';

/** Query parameter that carries the labels, in the same order as the boxes. */
export const HIGHLIGHT_LABELS_QUERY_PARAM = 'highlightLabels';

/** Query parameter that states the page rotation the boxes were measured at (Issue #2445). */
export const HIGHLIGHT_ROTATION_QUERY_PARAM = 'highlightRotation';

/** Optional unit marker for normalized 0–1000 boxes that do not require the PDF to build a link. */
export const HIGHLIGHT_UNITS_QUERY_PARAM = 'highlightUnits';

/** Query parameter that asks for a closer framing than the standard one (Issue #3256). */
export const HIGHLIGHT_ZOOM_QUERY_PARAM = 'highlightZoom';

/** The closest framing a link may ask for, relative to the standard framing. The server caps it too. */
export const MAX_HIGHLIGHT_ZOOM = 8;

/**
 * Every query parameter that describes the highlight. Code that rebuilds the URL and wants to
 * keep the highlight must carry all of them; carrying `highlight` alone would quietly reduce
 * several regions to the first one.
 */
export const HIGHLIGHT_QUERY_PARAMS = [
  HIGHLIGHT_QUERY_PARAM,
  HIGHLIGHT_ALSO_QUERY_PARAM,
  HIGHLIGHT_LABELS_QUERY_PARAM,
  HIGHLIGHT_ROTATION_QUERY_PARAM,
  HIGHLIGHT_UNITS_QUERY_PARAM,
  HIGHLIGHT_ZOOM_QUERY_PARAM,
] as const;

/** Separates one box (or one label) from the next. */
export const HIGHLIGHT_REGION_SEPARATOR = ';';

/**
 * Most regions one link may carry. A highlight is meant to direct attention; a link that names
 * more than this is a mistake or an attempt to build a very large overlay, and is ignored.
 */
export const MAX_HIGHLIGHT_REGIONS = 12;

/** Longest label kept from a link. Longer text is cut, so a link cannot cover the drawing. */
export const MAX_HIGHLIGHT_LABEL_LENGTH = 60;

/** A stable empty list, so "no regions" never looks like a change to Angular. */
export const NO_HIGHLIGHT_REGIONS: readonly HighlightRegion[] = Object.freeze([]);

/**
 * Parses one box written as `x,y,width,height`.
 *
 * The parsing is deliberately the same as the single-box parser it replaces (`parseInt` on each
 * of exactly four parts), so a link that worked before this issue resolves to the same box.
 */
export function parseHighlightBox(text: string): HighlightBox | null {
  const parts = text.split(',').map(part => parseInt(part, 10));
  if (parts.length !== 4 || parts.some(Number.isNaN)) {
    return null;
  }
  return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
}

/**
 * Parses the `highlight`, `highlightAlso` and `highlightLabels` query parameters into regions.
 *
 * Returns an empty list when the boxes cannot be read. The parse is all-or-nothing on purpose:
 * a link that says "these three regions" must not quietly show two of them, because the reader
 * would take the two for the whole evidence.
 */
export function parseHighlightRegions(highlight: unknown, also?: unknown, labels?: unknown): HighlightRegion[] {
  if (typeof highlight !== 'string') {
    return [];
  }
  const first = parseHighlightBox(highlight);
  if (!first) {
    return [];
  }
  const boxes: HighlightBox[] = [first];
  if (typeof also === 'string') {
    const segments = also
      .split(HIGHLIGHT_REGION_SEPARATOR)
      .map(segment => segment.trim())
      .filter(segment => segment.length > 0);
    for (const segment of segments) {
      const box = parseHighlightBox(segment);
      if (!box) {
        return [];
      }
      boxes.push(box);
    }
  } else if (also !== undefined && also !== null) {
    // Angular hands a repeated parameter over as an array. That is not a form this link has.
    return [];
  }
  if (boxes.length > MAX_HIGHLIGHT_REGIONS) {
    return [];
  }

  const labelList = typeof labels === 'string' ? labels.split(HIGHLIGHT_REGION_SEPARATOR) : [];
  return boxes.map((box, index) => {
    const label = cleanHighlightLabel(labelList[index]);
    return label ? { box, label } : { box };
  });
}

/**
 * Reads the `highlightRotation` query parameter.
 *
 * Returns `null` when the link does not state a rotation or states one that is not a quarter turn.
 * `null` is not the same as 0: it means the boxes' frame is unknown, and the caller falls back to
 * treating them as matching the page as displayed.
 */
export function parseHighlightRotation(value: unknown): 0 | 90 | 180 | 270 | null {
  if (typeof value !== 'string' || !/^\d{1,3}$/.test(value.trim())) {
    return null;
  }
  const degrees = parseInt(value.trim(), 10);
  return isQuarterTurn(degrees) ? degrees : null;
}

/** Reads the optional unit marker. Unknown and absent values preserve the historic point format. */
export function parseHighlightUnits(value: unknown): HighlightUnits {
  return value === 'permille' ? 'permille' : 'points';
}

/**
 * Reads the `highlightZoom` query parameter (Issue #3256): how much closer than the standard framing
 * to show the highlight, capped at `MAX_HIGHLIGHT_ZOOM`.
 *
 * Returns `null` for the standard framing: when the link has no zoom, asks for 1 or less, or says
 * something that is not a plain number.
 */
export function parseHighlightZoom(value: unknown): number | null {
  if (typeof value !== 'string' || !/^\d+(\.\d+)?$/.test(value.trim())) {
    return null;
  }
  const zoom = parseFloat(value.trim());
  return zoom > 1 ? Math.min(zoom, MAX_HIGHLIGHT_ZOOM) : null;
}

/** Whether a value is one of the four rotations a page can be displayed at. */
function isQuarterTurn(value: number | null | undefined): value is 0 | 90 | 180 | 270 {
  return value === 0 || value === 90 || value === 180 || value === 270;
}

/**
 * Builds the query parameters for a set of regions: the inverse of `parseHighlightRegions`.
 *
 * A `null` value removes that parameter when the result is merged into the current URL, which is
 * what clearing a highlight needs.
 *
 * @param rotation the page rotation the boxes were measured at: 0, 90, 180 or 270. Omit it, or
 *     pass `null`, when unknown. An unknown or unusable value leaves `highlightRotation` out of
 *     the link
 */
export function highlightQueryParams(
  regions: readonly HighlightRegion[],
  rotation?: number | null,
  units: HighlightUnits = 'points'
): {
  highlight: string | null;
  highlightAlso: string | null;
  highlightLabels: string | null;
  highlightRotation: string | null;
  highlightUnits: string | null;
  highlightZoom: null;
} {
  if (regions.length === 0) {
    return {
      highlight: null,
      highlightAlso: null,
      highlightLabels: null,
      highlightRotation: null,
      highlightUnits: null,
      highlightZoom: null,
    };
  }
  const boxes = regions.map(({ box }) =>
    [box.x, box.y, box.width, box.height].map(value => Math.round(value)).join(',')
  );

  // A label must not contain the separator, or the labels after it would move to the wrong box.
  const labels = regions.map(region => (cleanHighlightLabel(region.label) ?? '').replaceAll(';', ','));
  while (labels.length > 0 && labels[labels.length - 1] === '') {
    labels.pop();
  }
  return {
    highlight: boxes[0],
    highlightAlso: boxes.length > 1 ? boxes.slice(1).join(HIGHLIGHT_REGION_SEPARATOR) : null,
    highlightLabels: labels.length > 0 ? labels.join(HIGHLIGHT_REGION_SEPARATOR) : null,
    // Only a quarter turn is written. Any other value would be read back as "unknown", and a link
    // that says something its own reader discards is worse than one that says nothing.
    highlightRotation: isQuarterTurn(rotation) ? String(rotation) : null,
    // Point links remain byte-for-byte compatible with links written before normalized search
    // highlights. Only the new format needs to say which units it carries.
    highlightUnits: units === 'permille' ? units : null,
    // Only the assistant asks for a closer framing (Issue #3256). A link built here, and a dismissal
    // merged into the current URL, must not keep a zoom that was meant for an earlier highlight.
    highlightZoom: null,
  };
}

/** The smallest box that contains every given box, or `null` when there are none. */
export function unionBox(boxes: readonly HighlightBox[]): HighlightBox | null {
  if (boxes.length === 0) {
    return null;
  }
  const left = Math.min(...boxes.map(box => box.x));
  const top = Math.min(...boxes.map(box => box.y));
  const right = Math.max(...boxes.map(box => box.x + box.width));
  const bottom = Math.max(...boxes.map(box => box.y + box.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * Normalizes a label: one line, single spaces, and no longer than `MAX_HIGHLIGHT_LABEL_LENGTH`.
 * Returns `undefined` for a missing or blank label.
 */
export function cleanHighlightLabel(label: unknown): string | undefined {
  if (typeof label !== 'string') {
    return undefined;
  }
  const text = label.replace(/\s+/g, ' ').trim();
  if (text.length === 0) {
    return undefined;
  }
  return text.length > MAX_HIGHLIGHT_LABEL_LENGTH
    ? `${text.slice(0, MAX_HIGHLIGHT_LABEL_LENGTH - 1).trimEnd()}…`
    : text;
}
